import InlinePageHeading from './InlinePageHeading';
import InlinePageSectionHeading from './InlinePageSectionHeading';
import React from 'react';
import InlinePageSection from './InlinePageSection';

export interface PageField {
    component: InlinePageComponent;
    props: any;
    label?: string;
}

interface InlinePageComponent extends React.FunctionComponent<any> {
    calculateMaxHeightPercentage: () => number;
}

export default class InlinePage {
    readonly height: number;
    private readonly fields: PageField[];
    private readonly MAX_SPACE = 1;

    constructor(heading: string, height: number) {
        this.height = height;
        this.fields = [
            {
                component: InlinePageHeading,
                props: { heading: heading },
            },
        ];
    }

    appendField(field: PageField) {
        this.fields.push(this.removeLabelIfLastLabelWasTheSame(field));
    }

    lastFieldLabel(): string {
        for (let i: number = this.fields.length - 1; i >= 0; i--) {
            const field = this.fields[i];
            if (field.label) {
                return field.label;
            }
        }
        return '';
    }

    hasNoSpaceForField(field: PageField): boolean {
        return this.calcFilledSpace() + this.calcPotentialFieldSpace(field) > this.MAX_SPACE;
    }

    calcFilledSpace(): number {
        return this.fields.map(this.calcFieldSpace).reduce((sum, val) => sum + val, 0);
    }

    calcFieldSpace(field: PageField): number {
        const componentHeight = field.component.calculateMaxHeightPercentage();
        if (!field.label) {
            return componentHeight;
        }

        return InlinePageSectionHeading.calculateMaxHeightPercentage() + componentHeight;
    }

    calcPotentialFieldSpace(field: PageField): number {
        return this.calcFieldSpace(this.removeLabelIfLastLabelWasTheSame(field));
    }

    // This is to prevent duplicate label showing for the same type component
    removeLabelIfLastLabelWasTheSame(field: PageField): PageField {
        if (this.lastFieldLabel() === field.label) {
            return { ...field, label: undefined };
        }
        return field;
    }

    render(key: string): React.ReactNode {
        return (
            <div className="inline-page" key={key}>
                {this.renderFields(key)}
            </div>
        );
    }

    renderFields(pageKey: string): React.ReactNode[] {
        const renderedFields: React.ReactNode[] = [];

        this.fields.forEach((field, index) => {
            if (field.label) {
                renderedFields.push(
                    <InlinePageSection
                        parentHeight={this.height}
                        fillRatio={InlinePageSectionHeading.calculateMaxHeightPercentage()}
                        key={`page-${pageKey}-field-label-${index}`}
                    >
                        <InlinePageSectionHeading heading={field.label} />
                    </InlinePageSection>
                );
            }

            renderedFields.push(
                <InlinePageSection
                    parentHeight={this.height}
                    fillRatio={field.component.calculateMaxHeightPercentage()}
                    key={`page-${pageKey}-field-component-${index}`}
                >
                    {React.createElement(field.component, field.props)}
                </InlinePageSection>
            );
        });

        return renderedFields;
    }
}
